/**
 * Модель диаграммы Ганта КСП: дерево разделов WBS с работами, видимые строки (свёрнутые
 * разделы, отбор, поиск), окно шкалы по масштабу, деления шкалы, связи выбранной работы.
 * Модуль без импортов значений — проверяется node:test:
 *   cd frontend && npm run test:ksp
 */

export interface GanttActivity {
  id: string;
  code: string;
  name: string;
  wbs_id?: string | null;
  wbs_order?: number;
  kind?: string;
  quantity?: string | null;
  unit?: string | null;
  forecast_start?: string | null;
  forecast_finish?: string | null;
  baseline_start?: string | null;
  baseline_finish?: string | null;
  total_float_minutes?: number;
  critical?: boolean;
}

export interface GanttTiming {
  id: string;
  early_start: string;
  early_finish: string;
  total_float_minutes?: number;
  critical: boolean;
}

export interface GanttWbs {
  id: string;
  parent_id: string | null;
  code: string;
  name: string;
}

export interface GanttLink {
  predecessor_id: string;
  successor_id: string;
  /** FS, SS, FF, SF: первая буква — у предшественника, вторая — у последователя. */
  kind?: string;
}

/** Отрезок времени, миллисекунды эпохи. */
export interface Span {
  start: number;
  finish: number;
}

export interface Work<A extends GanttActivity = GanttActivity> {
  activity: A;
  forecast: Span | null;
  baseline: Span | null;
  float: number | null;
  critical: boolean;
  milestone: boolean;
}

export interface TreeNode<A extends GanttActivity = GanttActivity> {
  id: string;
  code: string;
  name: string;
  depth: number;
  parent: string | null;
  children: TreeNode<A>[];
  works: Work<A>[];
  /** Итоги по всем работам ниже узла. */
  span: Span | null;
  count: number;
  critical: number;
  negative: number;
  /** Наименьший резерв среди работ ниже узла, минуты; null — не рассчитан. */
  worst: number | null;
}

export interface Tree<A extends GanttActivity = GanttActivity> {
  roots: TreeNode<A>[];
  nodes: Map<string, TreeNode<A>>;
  works: Map<string, Work<A>>;
  /** Узел, в котором лежит работа. */
  owner: Map<string, string>;
  span: Span | null;
}

export const NO_WBS = '__no_wbs__';
export const DAY = 86400000;

export const naturalCompare = (a: string, b: string) =>
  a.localeCompare(b, 'ru', { numeric: true, sensitivity: 'base' });

const parse = (value: string | null | undefined) => {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
};

const spanOf = (start?: string | null, finish?: string | null): Span | null => {
  const s = parse(start);
  const f = parse(finish);
  return s == null || f == null ? null : { start: s, finish: Math.max(s, f) };
};

const merge = (a: Span | null, b: Span | null): Span | null =>
  !a ? b : !b ? a : { start: Math.min(a.start, b.start), finish: Math.max(a.finish, b.finish) };

/** Работа с прогнозом: из полей работы, иначе из расчёта (`timings`). */
export function toWork<A extends GanttActivity>(activity: A, timing?: GanttTiming): Work<A> {
  const forecast =
    spanOf(activity.forecast_start, activity.forecast_finish) ??
    (timing ? spanOf(timing.early_start, timing.early_finish) : null);
  return {
    activity,
    forecast,
    baseline: spanOf(activity.baseline_start, activity.baseline_finish),
    float: activity.total_float_minutes ?? timing?.total_float_minutes ?? null,
    critical: Boolean(activity.critical ?? timing?.critical),
    milestone: activity.kind === 'milestone',
  };
}

const emptyNode = <A extends GanttActivity>(
  id: string,
  code: string,
  name: string,
  parent: string | null,
): TreeNode<A> => ({
  id,
  code,
  name,
  depth: 0,
  parent,
  children: [],
  works: [],
  span: null,
  count: 0,
  critical: 0,
  negative: 0,
  worst: null,
});

/** Дерево разделов: узлы по коду, работы по порядку WBS и коду, итоги по поддереву. */
export function buildTree<A extends GanttActivity>(
  activities: A[],
  wbs: GanttWbs[],
  timings: GanttTiming[] = [],
): Tree<A> {
  const timingById = new Map(timings.map((t) => [t.id, t]));
  const nodes = new Map<string, TreeNode<A>>();
  for (const w of wbs) nodes.set(w.id, emptyNode<A>(w.id, w.code, w.name, w.parent_id));
  const works = new Map<string, Work<A>>();
  const owner = new Map<string, string>();
  for (const activity of activities) {
    const work = toWork(activity, timingById.get(activity.id));
    works.set(activity.id, work);
    const id = activity.wbs_id && nodes.has(activity.wbs_id) ? activity.wbs_id : NO_WBS;
    if (id === NO_WBS && !nodes.has(NO_WBS))
      nodes.set(NO_WBS, emptyNode<A>(NO_WBS, '', 'Без раздела', null));
    nodes.get(id)!.works.push(work);
    owner.set(activity.id, id);
  }
  const roots: TreeNode<A>[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent ? nodes.get(node.parent) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  const byCode = (a: TreeNode<A>, b: TreeNode<A>) =>
    a.id === NO_WBS ? 1 : b.id === NO_WBS ? -1 : naturalCompare(a.code, b.code);
  const seen = new Set<string>();
  const visit = (node: TreeNode<A>, depth: number) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    node.depth = depth;
    node.children.sort(byCode);
    node.works.sort(
      (a, b) =>
        (a.activity.wbs_order ?? 0) - (b.activity.wbs_order ?? 0) ||
        naturalCompare(a.activity.code, b.activity.code),
    );
    // Ребёнок, уже встреченный выше, — цикл по parent_id: ссылку обрываем.
    node.children = node.children.filter((child) => !seen.has(child.id));
    let span: Span | null = null;
    let count = 0;
    let critical = 0;
    let negative = 0;
    let worst: number | null = null;
    const least = (a: number | null, b: number | null) =>
      a == null ? b : b == null ? a : Math.min(a, b);
    for (const work of node.works) {
      span = merge(span, work.forecast);
      count += 1;
      if (work.critical) critical += 1;
      if ((work.float ?? 0) < 0) negative += 1;
      worst = least(worst, work.float);
    }
    for (const child of [...node.children]) {
      if (seen.has(child.id)) {
        node.children = node.children.filter((other) => other !== child);
        continue;
      }
      visit(child, depth + 1);
      span = merge(span, child.span);
      count += child.count;
      critical += child.critical;
      negative += child.negative;
      worst = least(worst, child.worst);
    }
    node.span = span;
    node.count = count;
    node.critical = critical;
    node.negative = negative;
    node.worst = worst;
  };
  roots.sort(byCode);
  for (const root of roots) visit(root, 0);
  // Узлы, замкнутые в цикл по parent_id, корнем не стали: показываем их как корни.
  for (const node of nodes.values())
    if (!seen.has(node.id)) {
      const parent = node.parent ? nodes.get(node.parent) : undefined;
      if (parent) parent.children = parent.children.filter((child) => child !== node);
      node.parent = null;
      roots.push(node);
      visit(node, 0);
    }
  const span = roots.reduce<Span | null>((acc, root) => merge(acc, root.span), null);
  return { roots, nodes, works, owner, span };
}

/**
 * Раскрыты корень и разделы первого уровня — проект открывается обзором объектов
 * (лот → разделы → свёрнутые объекты). «Без раздела» свёрнут, если есть другие разделы;
 * у графика совсем без WBS он единственный и раскрыт.
 */
export function defaultOpen(tree: Tree<GanttActivity>, depth = 2): Set<string> {
  const open = new Set<string>();
  const onlyLoose = tree.roots.length === 1 && tree.roots[0].id === NO_WBS;
  for (const node of tree.nodes.values())
    if (node.depth < depth && (node.id !== NO_WBS || onlyLoose)) open.add(node.id);
  return open;
}

export type Filter = 'all' | 'critical' | 'negative' | 'milestones';

/** Итоги раздела по показанным работам: при отборе — только по найденным. */
export interface Totals {
  shown: number;
  span: Span | null;
  worst: number | null;
  negative: number;
}

export type Row<A extends GanttActivity = GanttActivity> =
  | ({ kind: 'wbs'; id: string; depth: number; node: TreeNode<A>; open: boolean } & Totals)
  | { kind: 'work'; id: string; depth: number; work: Work<A> };

const passes = (work: Work, filter: Filter) =>
  filter === 'critical'
    ? work.critical
    : filter === 'negative'
      ? (work.float ?? 0) < 0
      : filter === 'milestones'
        ? work.milestone
        : true;

const found = (text: string, query: string) => !query || text.toLowerCase().includes(query);

/**
 * Строки на экран. Без отбора и поиска — по раскрытым разделам. С отбором или поиском —
 * только подходящие работы и путь разделов к ним (раскрытый, с числом найденного).
 * Поиск идёт по коду и названию работы и раздела: нашёлся раздел — показаны все его работы.
 */
export function visibleRows<A extends GanttActivity>(
  tree: Tree<A>,
  open: Set<string>,
  filter: Filter,
  search: string,
): Row<A>[] {
  const query = search.trim().toLowerCase();
  const rows: Row<A>[] = [];
  if (filter === 'all' && !query) {
    const walk = (node: TreeNode<A>) => {
      const isOpen = open.has(node.id);
      rows.push({
        kind: 'wbs',
        id: node.id,
        depth: node.depth,
        node,
        open: isOpen,
        shown: node.count,
        span: node.span,
        worst: node.worst,
        negative: node.negative,
      });
      if (!isOpen) return;
      for (const child of node.children) walk(child);
      for (const work of node.works)
        rows.push({ kind: 'work', id: work.activity.id, depth: node.depth + 1, work });
    };
    for (const root of tree.roots) walk(root);
    return rows;
  }
  const hit = (work: Work<A>, inherited: boolean) =>
    passes(work, filter) &&
    (inherited || found(`${work.activity.code} ${work.activity.name}`, query));
  const nodeHit = (node: TreeNode<A>) => query !== '' && found(`${node.code} ${node.name}`, query);
  const hits = new Map<string, Totals>();
  const count = (node: TreeNode<A>, inherited: boolean): Totals => {
    const all = inherited || nodeHit(node);
    const totals: Totals = { shown: 0, span: null, worst: null, negative: 0 };
    const add = (part: Totals) => {
      totals.shown += part.shown;
      totals.span = merge(totals.span, part.span);
      totals.worst =
        totals.worst == null
          ? part.worst
          : part.worst == null
            ? totals.worst
            : Math.min(totals.worst, part.worst);
      totals.negative += part.negative;
    };
    for (const work of node.works)
      if (hit(work, all))
        add({
          shown: 1,
          span: work.forecast,
          worst: work.float,
          negative: (work.float ?? 0) < 0 ? 1 : 0,
        });
    for (const child of node.children) add(count(child, all));
    hits.set(node.id, totals);
    return totals;
  };
  for (const root of tree.roots) count(root, false);
  const walk = (node: TreeNode<A>, inherited: boolean) => {
    const totals = hits.get(node.id);
    if (!totals?.shown) return;
    const all = inherited || nodeHit(node);
    rows.push({ kind: 'wbs', id: node.id, depth: node.depth, node, open: true, ...totals });
    for (const child of node.children) walk(child, all);
    for (const work of node.works)
      if (hit(work, all))
        rows.push({ kind: 'work', id: work.activity.id, depth: node.depth + 1, work });
  };
  for (const root of tree.roots) walk(root, false);
  return rows;
}

/** Цепочка разделов от корня до узла: «Лот / Базисный склад угля / Автовесовая». */
export function pathOf<A extends GanttActivity>(tree: Tree<A>, nodeId: string): TreeNode<A>[] {
  const path: TreeNode<A>[] = [];
  let node = tree.nodes.get(nodeId);
  const seen = new Set<string>();
  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    path.unshift(node);
    node = node.parent ? tree.nodes.get(node.parent) : undefined;
  }
  return path;
}

/** Все последователи работы по связям, без неё самой (для подсказки при переносе). */
export function successorsOf(links: GanttLink[], id: string): string[] {
  const next = new Map<string, string[]>();
  for (const link of links) {
    const list = next.get(link.predecessor_id) ?? [];
    list.push(link.successor_id);
    next.set(link.predecessor_id, list);
  }
  const affected = new Set<string>();
  const queue = [id];
  while (queue.length) {
    const current = queue.shift()!;
    for (const successor of next.get(current) ?? [])
      if (!affected.has(successor)) {
        affected.add(successor);
        queue.push(successor);
      }
  }
  affected.delete(id);
  return [...affected];
}

export type Zoom = 'day' | 'week' | 'month' | 'all';

/** Сколько дней видно при масштабе и сколько из них — до «якоря». */
const WINDOWS: Record<Exclude<Zoom, 'all'>, { days: number; before: number }> = {
  day: { days: 21, before: 3 },
  week: { days: 91, before: 7 },
  month: { days: 365, before: 30 },
};

/**
 * Смещение пояса объекта от UTC в момент `t`, миллисекунды (Иркутск — +8 ч). Шкала делится
 * по полуночам объекта: иначе работа «с 11 ноября» по Иркутску начиналась бы на шкале
 * 10 ноября в 16:00 UTC — на треть дня левее подписи.
 */
export function zoneOffset(timezone: string, t: number): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(new Date(t));
    const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const local = Date.UTC(
      pick('year'),
      pick('month') - 1,
      pick('day'),
      pick('hour'),
      pick('minute'),
      pick('second'),
    );
    return local - Math.floor(t / 1000) * 1000;
  } catch {
    return 0;
  }
}

/** Дата «ГГГГ-ММ-ДД» момента `t` в поясе объекта. */
export function localDate(t: number, timezone: string): string {
  const d = new Date(t + zoneOffset(timezone, t));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Начало дня «ГГГГ-ММ-ДД» в поясе объекта — момент ISO (для ограничения «не ранее»). */
export function dayStart(date: string, timezone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(utc - zoneOffset(timezone, utc)).toISOString();
}

/** Полночь дня, в который попадает момент, — по поясу со смещением `offset`. */
export function startOfDay(t: number, offset = 0): number {
  const d = new Date(t + offset);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - offset;
}

/** Окно шкалы: вокруг «якоря» (обычно сегодня) или весь проект с полями. */
export function windowFor(zoom: Zoom, anchor: number, project: Span | null, offset = 0): Span {
  if (zoom === 'all') {
    const span = project ?? { start: anchor - 30 * DAY, finish: anchor + 335 * DAY };
    const pad = Math.max((span.finish - span.start) * 0.03, DAY);
    return { start: span.start - pad, finish: span.finish + pad };
  }
  const { days, before } = WINDOWS[zoom];
  const start = startOfDay(anchor, offset) - before * DAY;
  return { start, finish: start + days * DAY };
}

/** Окно масштаба с центром в `center` — при смене масштаба видна та же дата. */
export function centered(zoom: Zoom, center: number, project: Span | null): Span {
  if (zoom === 'all') return windowFor('all', center, project);
  const half = (WINDOWS[zoom].days * DAY) / 2;
  return { start: center - half, finish: center + half };
}

/** Сдвиг окна на половину его длины. */
export function pan(window: Span, direction: -1 | 1): Span {
  const shift = ((window.finish - window.start) / 2) * direction;
  return { start: window.start + shift, finish: window.finish + shift };
}

/** Положение момента в окне, проценты (может выйти за 0–100). */
export const at = (t: number, window: Span) =>
  ((t - window.start) / Math.max(window.finish - window.start, 1)) * 100;

/** Положение отрезка в окне, проценты; null — целиком вне окна. Края обрезаются. */
export function place(span: Span, window: Span): { left: number; width: number } | null {
  const total = window.finish - window.start;
  if (total <= 0 || span.finish < window.start || span.start > window.finish) return null;
  const from = Math.max(span.start, window.start);
  const to = Math.min(span.finish, window.finish);
  return { left: ((from - window.start) / total) * 100, width: ((to - from) / total) * 100 };
}

export interface Tick {
  t: number;
  /** Мелкое деление (день, понедельник, месяц, квартал) — подпись в нижнем ряду. */
  minor: boolean;
  /** Крупное (начало месяца или года) — подпись в верхнем ряду. */
  major: boolean;
}

/**
 * Деления шкалы: мелкие (дни / понедельники / месяцы / кварталы) и крупные (начало месяца
 * или года) — по полуночам пояса объекта (`offset`). Подписи — в интерфейсе: дату деления
 * в поясе объекта даёт `new Date(t + offset)` в UTC-полях.
 */
export function ticks(range: Span, zoom: Zoom, offset = 0): Tick[] {
  const result: Tick[] = [];
  const window = { start: range.start + offset, finish: range.finish + offset };
  const total = window.finish - window.start;
  const every =
    zoom === 'day'
      ? 'day'
      : zoom === 'week'
        ? 'monday'
        : zoom === 'month' || total < 540 * DAY
          ? 'month'
          : 'quarter';
  const daily = every === 'day' || every === 'monday';
  let t = startOfDay(window.start);
  if (!daily) {
    const d = new Date(t);
    t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  }
  for (let guard = 0; t <= window.finish && guard < 800; guard += 1) {
    const d = new Date(t);
    const minor =
      every === 'day' ||
      (every === 'monday' && d.getUTCDay() === 1) ||
      (every === 'month' && d.getUTCDate() === 1) ||
      (every === 'quarter' && d.getUTCDate() === 1 && d.getUTCMonth() % 3 === 0);
    const major = daily ? d.getUTCDate() === 1 : d.getUTCDate() === 1 && d.getUTCMonth() === 0;
    if (t >= window.start && (minor || major)) result.push({ t: t - offset, minor, major });
    t = daily ? t + DAY : Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  }
  return result;
}

/** Шаг переноса работы перетаскиванием — по масштабу, дни. */
export const SNAP_DAYS: Record<Zoom, number> = { day: 1, week: 1, month: 7, all: 30 };

/** Сдвиг при перетаскивании: пиксели → миллисекунды, кратно шагу масштаба. */
export function dragShift(dx: number, width: number, window: Span, zoom: Zoom): number {
  const step = SNAP_DAYS[zoom] * DAY;
  const ms = (dx / Math.max(width, 1)) * (window.finish - window.start);
  return Math.round(ms / step) * step;
}

/** Календарные дни работы по прогнозу (вехе — 0). */
export function spanDays(work: Work<GanttActivity>): number | null {
  if (!work.forecast) return null;
  if (work.milestone) return 0;
  return Math.max(1, Math.round((work.forecast.finish - work.forecast.start) / DAY));
}

export interface LinkPath {
  key: string;
  /** Предшественник или последователь выбранной работы. */
  role: 'before' | 'after';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Линии связей выбранной работы на диаграмме: от предшественников к ней и от неё к
 * последователям. Только для работ, чьи строки сейчас видны (не в свёрнутом разделе).
 * x — проценты окна, y — номер строки.
 */
export function linkPaths(
  selected: string,
  links: GanttLink[],
  rowIndex: Map<string, number>,
  works: Map<string, Work<GanttActivity>>,
  window: Span,
): LinkPath[] {
  const result: LinkPath[] = [];
  for (const [n, link] of links.entries()) {
    const role =
      link.successor_id === selected ? 'before' : link.predecessor_id === selected ? 'after' : null;
    if (!role) continue;
    const from = works.get(link.predecessor_id)?.forecast;
    const to = works.get(link.successor_id)?.forecast;
    const y1 = rowIndex.get(link.predecessor_id);
    const y2 = rowIndex.get(link.successor_id);
    if (!from || !to || y1 == null || y2 == null) continue;
    const kind = (link.kind ?? 'FS').toUpperCase();
    const x1 = at(kind[0] === 'S' ? from.start : from.finish, window);
    const x2 = at(kind[1] === 'F' ? to.finish : to.start, window);
    result.push({ key: `${n}`, role, x1, y1, x2, y2 });
  }
  return result;
}
